import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, request } from '../lib/api.ts';
import { type AuthUser, setAccessToken, setUser } from '../lib/auth-store.ts';
import { useAuthUser } from '../lib/use-auth.ts';

interface LoginResponse {
  accessToken: string;
  user: AuthUser;
}

export function LoginPage(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = useAuthUser();
  const [email, setEmail] = useState('admin@content-engine.local');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (existing) return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await request<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      });
      setAccessToken(data.accessToken);
      setUser(data.user);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Network error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg)',
      }}
    >
      <div
        style={{
          width: 380,
          padding: 32,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'linear-gradient(135deg, var(--neon), var(--amber))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              color: 'var(--bg)',
              fontSize: 16,
            }}
          >
            CE
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>ContentEngine</div>
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: 'var(--neon)',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              v0.1 · dev
            </div>
          </div>
        </div>

        <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('auth.login.title')}</h1>
        <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 20 }}>
          {t('auth.login.subtitle')}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <label>{t('auth.login.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-row">
            <label>{t('auth.login.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="primary"
            disabled={submitting}
            style={{ width: '100%', marginTop: 8 }}
          >
            {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
